import { forwardRef } from "react";
import type { JSX } from "react";
import type { LucideIcon, LucideProps } from "lucide-react";

const ExtensionsGlyph: LucideIcon = forwardRef<SVGSVGElement, LucideProps>(
  ({ size = 24, strokeWidth = 2, ...rest }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <rect x="2.343" y="5.657" width="8" height="8" rx="1" />
      <rect x="2.343" y="13.657" width="8" height="8" rx="1" />
      <rect x="10.343" y="13.657" width="8" height="8" rx="1" />
      <rect
        x="12.75"
        y="4.75"
        width="6.5"
        height="6.5"
        rx="1"
        transform="rotate(45 16 8)"
      />
    </svg>
  ),
);
ExtensionsGlyph.displayName = "ExtensionsGlyph";

// Official Slack 2019 logo, recolored to currentColor so it inherits
// the surrounding text color (icon set is monochrome by design).
// Path data sourced from Wikipedia's public Slack_icon_2019.svg.
const SlackGlyph: LucideIcon = forwardRef<SVGSVGElement, LucideProps>(
  ({ size = 24, ...rest }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 127 127"
      fill="currentColor"
      {...rest}
    >
      <path d="M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80zm6.6 0c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z" />
      <path d="M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47zm0 6.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H13.9C6.6 60.1.7 54.2.7 46.9c0-7.3 5.9-13.2 13.2-13.2H47z" />
      <path d="M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9zm-6.6 0c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.9 6.5 72.8.6 80.1.6c7.3 0 13.2 5.9 13.2 13.2v33.1z" />
      <path d="M80.1 99.8c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.8h13.2zm0-6.6c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80.1z" />
    </svg>
  ),
);
SlackGlyph.displayName = "SlackGlyph";
import {
  Activity,
  Archive,
  ArchiveRestore,
  Anchor,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  Bell,
  BellOff,
  Bot,
  Brain,
  Briefcase,
  Building2,
  Calendar,
  Check,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleDot,
  CircleEllipsis,
  ClipboardCopy,
  Clock,
  Clock3,
  Code,
  CornerDownLeft,
  CornerUpLeft,
  EllipsisVertical,
  Eye,
  EyeOff,
  FileImage,
  FileText,
  Filter,
  Folder,
  Gift,
  Globe,
  Handshake,
  Hash,
  Heart,
  History,
  IdCard,
  Image,
  Inbox,
  Link,
  Lock,
  ListChecks,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Mic,
  Notebook,
  NotebookPen,
  Palette,
  PanelBottom,
  PanelRight,
  Paperclip,
  Pause,
  Package,
  Pencil,
  PenSquare,
  Phone,
  Pin,
  Loader,
  Plug,
  Plus,
  Puzzle,
  Repeat2,
  Scale,
  Search,
  Send,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Smile,
  Trash2,
  TrendingDown,
  User,
  UserRoundPlus,
  Users,
  Video,
  Maximize2,
  Minimize2,
  Monitor,
  Moon,
  Sun,
  Wallet,
  Webhook,
  X,
  Zap,
} from "lucide-react";

export type IconName =
  | "activity"
  | "archive"
  | "archive-restore"
  | "arrow-left"
  | "arrow-right"
  | "arrow-up"
  | "arrow-up-right"
  | "attach"
  | "bell"
  | "bell-off"
  | "bot"
  | "brain"
  | "building"
  | "calendar"
  | "check"
  | "check-square"
  | "chevron-down"
  | "chevron-right"
  | "chevron-up"
  | "circle"
  | "circle-alert"
  | "circle-check"
  | "circle-dot"
  | "clock"
  | "clock-3"
  | "close"
  | "code"
  | "corner-down-left"
  | "corner-up-left"
  | "contacts"
  | "copy"
  | "edit"
  | "ellipsis-vertical"
  | "extensions"
  | "file"
  | "file-image"
  | "filter"
  | "folder"
  | "gift"
  | "globe"
  | "handshake"
  | "hash"
  | "history"
  | "image"
  | "inbox"
  | "link"
  | "lock"
  | "loader"
  | "mail"
  | "map-pin"
  | "maximize-2"
  | "minimize-2"
  | "menu"
  | "message"
  | "message-circle"
  | "mic"
  | "monitor"
  | "moon"
  | "more"
  | "note"
  | "notebook-pen"
  | "paperclip"
  | "pause"
  | "pencil"
  | "phone"
  | "pin"
  | "plus"
  | "puzzle"
  | "package"
  | "search"
  | "send"
  | "slack"
  | "settings"
  | "shield-alert"
  | "briefcase"
  | "chevron-left"
  | "eye"
  | "eye-off"
  | "heart"
  | "id-card"
  | "repeat-2"
  | "scale"
  | "shield-check"
  | "anchor"
  | "plug"
  | "palette"
  | "panel-bottom"
  | "panel-right"
  | "webhook"
  | "smile"
  | "sun"
  | "tasks"
  | "trash"
  | "trending-down"
  | "user"
  | "users"
  | "video"
   
  | "wallet"
  | "zap";

export interface IconProps {
  readonly name: IconName;
  readonly size?: number;
  readonly className?: string;
}

const iconByName: Record<IconName, LucideIcon> = {
  activity: Activity,
  archive: Archive,
  "archive-restore": ArchiveRestore,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "arrow-up": ArrowUp,
  "arrow-up-right": ArrowUpRight,
  attach: Paperclip,
  bell: Bell,
  "bell-off": BellOff,
  bot: Bot,
  brain: Brain,
  building: Building2,
  calendar: Calendar,
  check: Check,
  "check-square": CheckSquare,
  "chevron-down": ChevronDown,
  "chevron-right": ChevronRight,
  "chevron-up": ChevronUp,
  circle: Circle,
  "circle-alert": CircleAlert,
  "circle-check": CircleCheck,
  "circle-dot": CircleDot,
  clock: Clock,
  "clock-3": Clock3,
  close: X,
  code: Code,
  "corner-down-left": CornerDownLeft,
  "corner-up-left": CornerUpLeft,
  contacts: UserRoundPlus,
  copy: ClipboardCopy,
  edit: PenSquare,
  "ellipsis-vertical": EllipsisVertical,
  extensions: ExtensionsGlyph,
  file: FileText,
  "file-image": FileImage,
  filter: Filter,
  folder: Folder,
  gift: Gift,
  globe: Globe,
  handshake: Handshake,
  hash: Hash,
  history: History,
  image: Image,
  inbox: Inbox,
  link: Link,
  lock: Lock,
  loader: Loader,
  mail: Mail,
  "maximize-2": Maximize2,
  "minimize-2": Minimize2,
  "map-pin": MapPin,
  menu: Menu,
  message: MessageCircle,
  "message-circle": MessageCircle,
  mic: Mic,
  monitor: Monitor,
  moon: Moon,
  more: CircleEllipsis,
  note: Notebook,
  "notebook-pen": NotebookPen,
  paperclip: Paperclip,
  pause: Pause,
  pencil: Pencil,
  phone: Phone,
  pin: Pin,
  plus: Plus,
  puzzle: Puzzle,
  package: Package,
  search: Search,
  send: Send,
  slack: SlackGlyph,
  settings: Settings,
  "shield-alert": ShieldAlert,
  briefcase: Briefcase,
  "chevron-left": ChevronLeft,
  eye: Eye,
  "eye-off": EyeOff,
  heart: Heart,
  "id-card": IdCard,
  "repeat-2": Repeat2,
  scale: Scale,
  "shield-check": ShieldCheck,
  anchor: Anchor,
  plug: Plug,
  palette: Palette,
  "panel-bottom": PanelBottom,
  "panel-right": PanelRight,
  webhook: Webhook,
  smile: Smile,
  sun: Sun,
  tasks: ListChecks,
  trash: Trash2,
  "trending-down": TrendingDown,
  user: User,
  users: Users,
  video: Video,
  wallet: Wallet,
  zap: Zap,
};

// eslint-disable-next-line react-refresh/only-export-components
export function isIconName(value: string): value is IconName {
  return Object.prototype.hasOwnProperty.call(iconByName, value);
}

export function Icon({ name, size = 16, className }: IconProps): JSX.Element {
  const ResolvedIcon = iconByName[name];

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!ResolvedIcon) {
    return (
      <span
        className={`inline-flex items-center justify-center ${className ?? ""}`}
        aria-hidden="true"
        style={{ width: size, height: size }}
        title={`Missing icon: ${name}`}
      >
        ?
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-center ${className ?? ""}`}
      aria-hidden="true"
      style={{ width: size, height: size }}
    >
      <ResolvedIcon size={size} strokeWidth={1.6} />
    </span>
  );
}
